import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Stack,
  Chip,
  TextField,
  MenuItem,
  Typography,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { checkRole } from '../../pages/Typography/typographyValidation';

const WEIGHT_LABEL = { 400: '400 Regular', 500: '500 Medium', 600: '600 Semibold', 700: '700 Bold' };
const LEVEL_COLOR = { error: 'error.main', warning: 'warning.dark', info: 'text.secondary' };

const helperProps = (issue) => ({ sx: { mx: 0, color: issue ? LEVEL_COLOR[issue.level] : undefined } });

function SizeField({ label, value, issue, onChange, disabled }) {
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      error={!disabled && issue?.level === 'error'}
      helperText={(!disabled && issue?.message) || ' '}
      FormHelperTextProps={helperProps(issue)}
      InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
      inputProps={{ step: 1, min: 0 }}
      sx={{ width: 168 }}
    />
  );
}

SizeField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  issue: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

// One editable typography role: desktop/mobile size, line height, weight, case.
// `disabled` shows the values read-only (a page group inheriting the site-wide
// setting); `action` renders next to the label (the group "Override" switch);
// `highlight` briefly marks the row (clicking a label in the preview jumps here).
function TypeRoleRow({ role, value = {}, limits, onChange, disabled = false, action = null, highlight = false }) {
  const issues = disabled ? {} : checkRole(role, value, limits);
  const set = (field) => (v) => onChange(role.key, field, v);

  return (
    <Box
      id={`type-role-${role.key}`}
      sx={{
        py: 2,
        px: 1,
        mx: -1,
        borderRadius: 1,
        transition: 'background-color 0.6s',
        bgcolor: highlight ? 'warning.lighter' : 'transparent',
        scrollMarginTop: 16,
        '& + &': { borderTop: (t) => `1px solid ${t.palette.divider}` },
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={3}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1">{role.label}</Typography>
            {role.notOnSite && <Chip size="small" label="Not on site yet" />}
            {action}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {role.usage}
          </Typography>
          {role.currentSite && (
            <Typography variant="caption" color="text.disabled">
              Today: {role.currentSite}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} lg={9}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <SizeField label="Desktop" value={value.sizeDesktop} issue={issues.sizeDesktop} onChange={set('sizeDesktop')} disabled={disabled} />
            <SizeField label="Mobile" value={value.sizeMobile} issue={issues.sizeMobile} onChange={set('sizeMobile')} disabled={disabled} />
            <TextField
              size="small"
              type="number"
              label="Line height"
              value={value.lineHeight ?? ''}
              onChange={(e) => set('lineHeight')(e.target.value)}
              disabled={disabled}
              error={issues.lineHeight?.level === 'error'}
              helperText={issues.lineHeight?.message || ' '}
              FormHelperTextProps={helperProps(issues.lineHeight)}
              inputProps={{ step: 0.05, min: limits.lineHeightMin, max: limits.lineHeightMax }}
              sx={{ width: 112 }}
            />
            <TextField
              select
              size="small"
              label="Weight"
              value={value.weight ?? ''}
              onChange={(e) => set('weight')(Number(e.target.value))}
              disabled={disabled}
              helperText=" "
              sx={{ width: 150 }}
            >
              {limits.weights.map((w) => (
                <MenuItem key={w} value={w}>
                  {WEIGHT_LABEL[w] || w}
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={value.textTransform || 'none'}
              onChange={(_, v) => v && set('textTransform')(v)}
              disabled={disabled}
              sx={{ height: 40 }}
            >
              <ToggleButton value="none" sx={{ textTransform: 'none', px: 1.5 }}>
                Normal
              </ToggleButton>
              <ToggleButton value="uppercase" sx={{ textTransform: 'none', px: 1.5 }}>
                ALL CAPS
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

TypeRoleRow.propTypes = {
  role: PropTypes.object.isRequired,
  value: PropTypes.object,
  limits: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  action: PropTypes.node,
  highlight: PropTypes.bool,
};

export default memo(TypeRoleRow);
